import React, { useState, useEffect, useRef } from 'react';
import SMSF_RATES_CONFIG from './SMSF_Rates_Config.js';
import jsPDF from 'jspdf';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';

const SMSFAssetValuationManager = ({ smsfState, setSmsfState, onAllocateToMembers, onValuationChange }) => {
    const [selectedAssetType, setSelectedAssetType] = useState('property');
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [valuationHistory, setValuationHistory] = useState([]);

    // Asset types and their specific requirements
    const assetTypes = {
        property: {
            name: 'Real Estate',
            icon: '🏠',
            description: 'Residential, commercial, and industrial properties',
            valuationMethods: ['Independent Valuation', 'Market Comparison', 'Cost Approach', 'Income Approach'],
            complianceRequirements: ['Annual valuation required', 'Independent valuer if >$300K', 'Documentation required'],
            maxInHousePercentage: 5
        },
        collectibles: {
            name: 'Collectibles & Art',
            icon: '🎨',
            description: 'Art, antiques, coins, stamps, wine, and other collectibles',
            valuationMethods: ['Independent Valuation', 'Auction Records', 'Expert Appraisal', 'Insurance Valuation'],
            complianceRequirements: ['Annual valuation required', 'Independent valuer mandatory', 'Special storage rules'],
            maxInHousePercentage: 5
        },
        shares: {
            name: 'Shares & Securities',
            icon: '📈',
            description: 'Listed shares, bonds, and other securities',
            valuationMethods: ['Market Price', 'Last Trade Price', 'Volume Weighted Average'],
            complianceRequirements: ['Daily valuation available', 'Market price basis', 'Documentation required'],
            maxInHousePercentage: 5
        },
        alternatives: {
            name: 'Alternative Investments',
            icon: '⚡',
            description: 'Infrastructure, commodities, private equity, hedge funds',
            valuationMethods: ['Independent Valuation', 'NAV (Net Asset Value)', 'Market Comparison', 'DCF Analysis'],
            complianceRequirements: ['Quarterly valuation minimum', 'Independent valuer required', 'Detailed documentation'],
            maxInHousePercentage: 5
        },
        cash: {
            name: 'Cash & Deposits',
            icon: '💰',
            description: 'Bank deposits, term deposits, cash accounts',
            valuationMethods: ['Face Value', 'Accrued Interest', 'Market Value'],
            complianceRequirements: ['Monthly valuation', 'Interest accrual', 'Bank statements required'],
            maxInHousePercentage: 0
        }
    };

    // Sample asset data structure
    // Load assets from localStorage or use defaults
    const getInitialAssets = () => {
        try {
            const saved = localStorage.getItem('smsf_asset_valuation_assets');
            if (saved) {
                return JSON.parse(saved);
            }
        } catch (error) {
            console.error('Error loading assets from localStorage:', error);
        }
        
        // Default sample data
        return {
            property: [
                {
                    id: 1,
                    name: 'Commercial Office Building',
                    description: 'CBD office building',
                    purchaseDate: '2020-01-15',
                    purchasePrice: 2500000,
                    currentValue: 2800000,
                    valuationDate: '2024-01-15',
                    valuationMethod: 'Independent Valuation',
                    valuer: 'ABC Valuations Pty Ltd',
                    nextValuation: '2025-01-15',
                    location: 'Sydney CBD',
                    yield: 6.5,
                    capitalGrowth: 4.0,
                    loanAmount: 1400000,
                    loanToValue: 50,
                    interestRate: 6.25,
                    loanTerm: 20
                }
            ],
        collectibles: [
            {
                id: 1,
                name: 'Australian Art Collection',
                description: 'Collection of contemporary Australian art',
                purchaseDate: '2019-06-10',
                purchasePrice: 150000,
                currentValue: 180000,
                valuationDate: '2024-01-15',
                valuationMethod: 'Independent Valuation',
                valuer: 'Art Valuations Australia',
                nextValuation: '2025-01-15',
                category: 'Contemporary Art',
                storageLocation: 'Climate-controlled facility'
            }
        ],
        shares: [
            {
                id: 1,
                name: 'CBA Shares',
                description: 'Commonwealth Bank of Australia',
                purchaseDate: '2021-03-20',
                purchasePrice: 85000,
                currentValue: 92000,
                valuationDate: '2024-12-15',
                valuationMethod: 'Market Price',
                valuer: 'ASX Market Data',
                nextValuation: 'Daily',
                ticker: 'CBA',
                quantity: 1000,
                dividendYield: 4.2
            }
        ],
        alternatives: [
            {
                id: 1,
                name: 'Infrastructure Fund',
                description: 'Unlisted infrastructure investment',
                purchaseDate: '2022-05-15',
                purchasePrice: 500000,
                currentValue: 520000,
                valuationDate: '2024-09-30',
                valuationMethod: 'NAV',
                valuer: 'Fund Manager',
                nextValuation: '2024-12-31',
                fundType: 'Infrastructure',
                managementFee: 1.5
            }
        ],
        cash: [
            {
                id: 1,
                name: 'Term Deposit',
                description: '12-month term deposit',
                purchaseDate: '2024-01-15',
                purchasePrice: 100000,
                currentValue: 100000,
                valuationDate: '2024-12-15',
                valuationMethod: 'Face Value',
                valuer: 'Bank Statement',
                nextValuation: 'Monthly',
                bank: 'Westpac',
                interestRate: 4.5,
                maturityDate: '2025-01-15'
            }
        ]
        };
    };

    const [assets, setAssets] = useState(getInitialAssets());

    // Save assets to localStorage whenever they change
    useEffect(() => {
        try {
            localStorage.setItem('smsf_asset_valuation_assets', JSON.stringify(assets));
        } catch (error) {
            console.error('Error saving assets to localStorage:', error);
        }
    }, [assets]);
    
    // Call onValuationChange callback whenever assets change
    useEffect(() => {
        if (!onValuationChange) return;
        
        // Calculate totals by asset type
        const assetValues = {
            property: 0,
            shares: 0,
            alternatives: 0,
            collectibles: 0,
            cash: 0
        };
        
        // Sum up current values by type
        Object.entries(assets).forEach(([type, assetList]) => {
            assetList.forEach(asset => {
                assetValues[type] += asset.currentValue || 0;
            });
        });
        
        // Calculate productive assets (shares, alternatives, cash)
        const productiveAssets = {
            shares: assetValues.shares,
            alternatives: assetValues.alternatives,
            cash: assetValues.cash
        };
        
        // Extract property details (assuming first property for now)
        const commercialProperties = assets.property?.filter(p => p.description?.toLowerCase().includes('commercial') || p.description?.toLowerCase().includes('office')) || [];
        const residentialProperties = assets.property?.filter(p => !p.description?.toLowerCase().includes('commercial') && !p.description?.toLowerCase().includes('office')) || [];
        
        const commercialProperty = commercialProperties[0] || assets.property?.[0];
        const residentialProperty = residentialProperties[0];
        
        const commercialPropertyValue = commercialProperty?.currentValue || 0;
        const commercialPropertyYield = commercialProperty?.yield || 0;
        const commercialRentalIncome = commercialPropertyValue * (commercialPropertyYield / 100);
        const commercialPropertyCapitalGrowth = commercialProperty?.capitalGrowth || 4;
        
        const residentialPropertyValue = residentialProperty?.currentValue || 0;
        const residentialPropertyYield = residentialProperty?.yield || 0;
        const residentialRentalIncome = residentialPropertyValue * (residentialPropertyYield / 100);
        const residentialPropertyCapitalGrowth = residentialProperty?.capitalGrowth || 4;
        
        // Build valuation data object
        const valuationData = {
            assetValues,
            productiveAssets,
            commercialPropertyValue,
            commercialRentalIncome,
            commercialPropertyRentalYield: commercialPropertyYield,
            commercialPropertyCapitalGrowth,
            residentialPropertyValue,
            residentialRentalIncome,
            residentialPropertyRentalYield: residentialPropertyYield,
            residentialPropertyCapitalGrowth,
            assetDetails: {
                shares: assets.shares || [],
                alternatives: assets.alternatives || []
            }
        };
        
        console.log('📊 Valuation data calculated:', valuationData);
        onValuationChange(valuationData);
    }, [assets, onValuationChange]);

    // Valuation compliance checking
    const checkValuationCompliance = () => {
        const issues = [];
        const currentDate = new Date();
        
        Object.entries(assets).forEach(([assetType, assetList]) => {
            assetList.forEach(asset => {
                const nextValuationDate = new Date(asset.nextValuation);
                const daysUntilValuation = Math.ceil((nextValuationDate - currentDate) / (1000 * 60 * 60 * 24));
                
                if (daysUntilValuation < 0) {
                    issues.push({
                        type: 'Overdue',
                        asset: asset.name,
                        assetType: assetType,
                        daysOverdue: Math.abs(daysUntilValuation),
                        priority: 'High'
                    });
                } else if (daysUntilValuation <= 30) {
                    issues.push({
                        type: 'Due Soon',
                        asset: asset.name,
                        assetType: assetType,
                        daysUntilDue: daysUntilValuation,
                        priority: 'Medium'
                    });
                }
            });
        });
        
        return issues;
    };

    const complianceIssues = checkValuationCompliance();

    // Calculate total portfolio value
    const calculateTotalPortfolioValue = () => {
        let total = 0;
        Object.values(assets).forEach(assetList => {
            assetList.forEach(asset => {
                total += asset.currentValue;
            });
        });
        return total;
    };

    const totalPortfolioValue = calculateTotalPortfolioValue();

    // In-house asset compliance check
    const checkInHouseAssetCompliance = () => {
        const inHouseAssets = [...assets.property, ...assets.collectibles];
        const inHouseValue = inHouseAssets.reduce((sum, asset) => sum + asset.currentValue, 0);
        const inHousePercentage = (inHouseValue / totalPortfolioValue) * 100;
        
        return {
            totalValue: inHouseValue,
            percentage: inHousePercentage,
            isCompliant: inHousePercentage <= 5,
            overLimit: inHousePercentage > 5 ? inHousePercentage - 5 : 0
        };
    };

    const inHouseAssetCompliance = checkInHouseAssetCompliance();

    // Add new asset
    const addAsset = (assetType) => {
        const newAsset = {
            id: Date.now(),
            name: '',
            description: '',
            purchaseDate: '',
            purchasePrice: 0,
            currentValue: 0,
            valuationDate: new Date().toISOString().split('T')[0],
            valuationMethod: assetTypes[assetType].valuationMethods[0],
            valuer: '',
            nextValuation: ''
        };
        
        // Add property-specific defaults
        if (assetType === 'property') {
            newAsset.yield = 4.5;  // Default rental yield %
            newAsset.capitalGrowth = 4.0;  // Default capital growth %
            newAsset.loanAmount = 0;  // Default no loan
            newAsset.loanToValue = 0;  // Default LVR
            newAsset.interestRate = 6.25;  // Default interest rate %
            newAsset.loanTerm = 25;  // Default loan term years
        }
        
        setAssets(prev => ({
            ...prev,
            [assetType]: [...prev[assetType], newAsset]
        }));
    };

    // Update asset
    const updateAsset = (assetType, assetId, field, value) => {
        setAssets(prev => ({
            ...prev,
            [assetType]: prev[assetType].map(asset =>
                asset.id === assetId ? { ...asset, [field]: value } : asset
            )
        }));
    };

    // Delete asset
    const deleteAsset = (assetType, assetId) => {
        setAssets(prev => ({
            ...prev,
            [assetType]: prev[assetType].filter(asset => asset.id !== assetId)
        }));
    };

    // Calculate and update smsfState with asset values whenever assets change
    // Guard against infinite update loops by only emitting when the payload actually changes
    const lastEmittedRef = useRef(null);
    useEffect(() => {
        
        if (!setSmsfState) {
            console.log('  SKIPPING: setSmsfState is undefined');
            return;
        }

        // Calculate property values and rental income
        let residentialPropertyValue = 0;
        let residentialRentalIncome = 0;
        let residentialWeightedCapitalGrowth = 0;
        let commercialPropertyValue = 0;
        let commercialRentalIncome = 0;
        let commercialWeightedCapitalGrowth = 0;
        let totalLoanAmount = 0;
        let totalWeightedInterestRate = 0;
        let totalWeightedLoanTerm = 0;
        
        assets.property.forEach(property => {
            const value = property.currentValue || 0;
            const yieldPercent = property.yield || 0;
            const capitalGrowthPercent = property.capitalGrowth || 0;
            const annualRentalIncome = value * (yieldPercent / 100);
            const loanAmount = property.loanAmount || 0;
            const interestRate = property.interestRate || 0;
            const loanTerm = property.loanTerm || 0;
            
            // Accumulate loan data
            totalLoanAmount += loanAmount;
            if (loanAmount > 0) {
                totalWeightedInterestRate += loanAmount * interestRate;
                totalWeightedLoanTerm += loanAmount * loanTerm;
            }
            
            // Determine if commercial or residential based on description/name
            const description = (property.description || '').toLowerCase();
            const name = (property.name || '').toLowerCase();
            const isCommercial = description.includes('commercial') || 
                               description.includes('office') || 
                               description.includes('retail') ||
                               description.includes('industrial') ||
                               name.includes('commercial');
            
            if (isCommercial) {
                commercialPropertyValue += value;
                commercialRentalIncome += annualRentalIncome;
                commercialWeightedCapitalGrowth += value * capitalGrowthPercent;
            } else {
                residentialPropertyValue += value;
                residentialRentalIncome += annualRentalIncome;
                residentialWeightedCapitalGrowth += value * capitalGrowthPercent;
            }
        });
        
        // Calculate weighted average capital growth rates
        const residentialCapitalGrowth = residentialPropertyValue > 0 
            ? residentialWeightedCapitalGrowth / residentialPropertyValue 
            : 0;
        const commercialCapitalGrowth = commercialPropertyValue > 0 
            ? commercialWeightedCapitalGrowth / commercialPropertyValue 
            : 0;
        
        // Calculate weighted average loan metrics
        const avgInterestRate = totalLoanAmount > 0 
            ? totalWeightedInterestRate / totalLoanAmount 
            : 0;
        const avgLoanTerm = totalLoanAmount > 0 
            ? totalWeightedLoanTerm / totalLoanAmount 
            : 0;

        // Calculate totals for each asset type
        const shareValue = assets.shares.reduce((sum, asset) => sum + (asset.currentValue || 0), 0);
        const collectiblesValue = assets.collectibles.reduce((sum, asset) => sum + (asset.currentValue || 0), 0);
        const cashValue = assets.cash.reduce((sum, asset) => sum + (asset.currentValue || 0), 0);
        const alternativesValue = assets.alternatives.reduce((sum, asset) => sum + (asset.currentValue || 0), 0);
        
        // Calculate rental yield percentages
        const residentialRentalYield = residentialPropertyValue > 0 
            ? (residentialRentalIncome / residentialPropertyValue) * 100 
            : 0;
        const commercialRentalYield = commercialPropertyValue > 0 
            ? (commercialRentalIncome / commercialPropertyValue) * 100 
            : 0;
        
        // Update smsfState with calculated values
        // setSmsfState can be either a real setState function OR a callback
        // So we just pass the new state object directly
        const propertyTotal = residentialPropertyValue + commercialPropertyValue;
        
        const nextPayload = {
            assetValues: {
                property: propertyTotal,
                cash: cashValue,
                shares: shareValue,
                alternatives: alternativesValue,
                collectibles: collectiblesValue
            },
            residentialPropertyValue,
            residentialRentalIncome,
            residentialPropertyRentalYield: residentialRentalYield,
            residentialPropertyCapitalGrowth: residentialCapitalGrowth,
            commercialPropertyValue,
            commercialRentalIncome,
            commercialPropertyRentalYield: commercialRentalYield,
            commercialPropertyCapitalGrowth: commercialCapitalGrowth,
            // Loan data (LRBA)
            totalLoanAmount: totalLoanAmount,
            avgInterestRate: avgInterestRate,
            avgLoanTerm: avgLoanTerm,
            // Store individual productive asset values for Advanced calculator
            productiveAssets: {
                shares: shareValue,
                alternatives: alternativesValue,
                cash: cashValue
            },
            // Store detailed asset lists for transfer
            assetDetails: {
                shares: assets.shares.map(s => ({ name: s.name, value: s.currentValue, dividendYield: s.dividendYield })),
                alternatives: assets.alternatives.map(a => ({ name: a.name, value: a.currentValue, managementFee: a.managementFee }))
            },
            // NEW: Pass complete assets object for multi-asset transfer
            assets: assets
        };
        
        // Shallow-stable stringify compare (payload is small and flat enough)
        const serialized = JSON.stringify(nextPayload);
        if (serialized !== lastEmittedRef.current) {
            lastEmittedRef.current = serialized;
            setSmsfState(nextPayload);
        }
    }, [assets]);

    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Asset Valuation Manager</h2>
                <button
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                    {showAdvanced ? 'Hide' : 'Show'} Advanced Options
                </button>
            </div>

            {/* Portfolio Summary */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Portfolio Summary</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                        <div className="text-2xl font-bold text-blue-600">
                            ${totalPortfolioValue.toLocaleString()}
                        </div>
                        <div className="text-sm text-blue-700">Total Portfolio Value</div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg">
                        <div className="text-2xl font-bold text-green-600">
                            {inHouseAssetCompliance.percentage.toFixed(1)}%
                        </div>
                        <div className="text-sm text-green-700">In-House Assets</div>
                    </div>
                    <div className="text-center p-4 bg-purple-50 rounded-lg">
                        <div className="text-2xl font-bold text-purple-600">
                            {complianceIssues.length}
                        </div>
                        <div className="text-sm text-purple-700">Valuation Issues</div>
                    </div>
                    <div className={`text-center p-4 rounded-lg ${
                        inHouseAssetCompliance.isCompliant ? 'bg-green-50' : 'bg-red-50'
                    }`}>
                        <div className={`text-2xl font-bold ${
                            inHouseAssetCompliance.isCompliant ? 'text-green-600' : 'text-red-600'
                        }`}>
                            {inHouseAssetCompliance.isCompliant ? '✅' : '⚠️'}
                        </div>
                        <div className={`text-sm ${
                            inHouseAssetCompliance.isCompliant ? 'text-green-700' : 'text-red-700'
                        }`}>
                            In-House Compliance
                        </div>
                    </div>
                </div>
            </div>

            {/* Compliance Alerts */}
            {complianceIssues.length > 0 && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Valuation Compliance Alerts</h3>
                    <div className="space-y-3">
                        {complianceIssues.map((issue, index) => (
                            <div key={index} className={`p-4 border-l-4 rounded-lg ${
                                issue.priority === 'High' ? 'border-red-500 bg-red-50' : 'border-yellow-500 bg-yellow-50'
                            }`}>
                                <div className="flex items-center justify-between">
                                    <div>
                                        <h4 className="font-semibold">{issue.asset}</h4>
                                        <p className="text-sm text-gray-600">
                                            {issue.type === 'Overdue' 
                                                ? `${issue.daysOverdue} days overdue`
                                                : `Due in ${issue.daysUntilDue} days`
                                            }
                                        </p>
                                    </div>
                                    <span className={`px-2 py-1 text-xs rounded ${
                                        issue.priority === 'High' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'
                                    }`}>
                                        {issue.priority}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Asset Type Navigation */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Asset Categories</h3>
                <div className="flex flex-wrap gap-2 mb-6">
                    {Object.entries(assetTypes).map(([key, assetType]) => (
                        <button
                            key={key}
                            onClick={() => setSelectedAssetType(key)}
                            className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${
                                selectedAssetType === key
                                    ? 'bg-blue-600 text-white'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                            }`}
                        >
                            <span>{assetType.icon}</span>
                            <span>{assetType.name}</span>
                            <span className="text-xs bg-gray-400 text-white px-2 py-1 rounded">
                                {assets[key].length}
                            </span>
                        </button>
                    ))}
                </div>

                {/* Asset List */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <h4 className="font-semibold">
                            {assetTypes[selectedAssetType].name} Assets
                        </h4>
                        <div className="flex gap-2">
                            <button
                                onClick={() => addAsset(selectedAssetType)}
                                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                            >
                                + Add Another Asset
                            </button>
                            <button
                                onClick={() => alert('All assets saved successfully!')}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                            >
                                Save All
                            </button>
                        </div>
                    </div>

                    {assets[selectedAssetType].length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            <div className="text-4xl mb-2">{assetTypes[selectedAssetType].icon}</div>
                            <p>No {assetTypes[selectedAssetType].name.toLowerCase()} assets added yet</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {assets[selectedAssetType].map((asset) => (
                                <div key={asset.id} className="border border-gray-200 rounded-lg p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Asset Type</label>
                                            <input
                                                type="text"
                                                value={assetTypes[selectedAssetType].name}
                                                disabled
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Asset Description</label>
                                            <input
                                                type="text"
                                                value={asset.description || ''}
                                                onChange={(e) => updateAsset(selectedAssetType, asset.id, 'description', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="e.g., 123 Main St"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Date</label>
                                            <input
                                                type="date"
                                                value={asset.purchaseDate || ''}
                                                onChange={(e) => updateAsset(selectedAssetType, asset.id, 'purchaseDate', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Purchase Price ($)</label>
                                            <input
                                                {...getNumericInputProps()}
                                                value={asset.purchasePrice}
                                                onChange={(e) => updateAsset(selectedAssetType, asset.id, 'purchasePrice', parseFloat(e.target.value) || 0)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="500000"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Current Valuation ($)</label>
                                            <input
                                                {...getNumericInputProps()}
                                                value={asset.currentValue}
                                                onChange={(e) => updateAsset(selectedAssetType, asset.id, 'currentValue', parseFloat(e.target.value) || 0)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="650000"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Valuation Date</label>
                                            <input
                                                type="date"
                                                value={asset.valuationDate || ''}
                                                onChange={(e) => updateAsset(selectedAssetType, asset.id, 'valuationDate', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Valuation Method</label>
                                            <select
                                                value={asset.valuationMethod}
                                                onChange={(e) => updateAsset(selectedAssetType, asset.id, 'valuationMethod', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            >
                                                {assetTypes[selectedAssetType].valuationMethods.map((method) => (
                                                    <option key={method} value={method}>{method}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Valuer Details (name, credentials)</label>
                                            <input
                                                type="text"
                                                value={asset.valuer || ''}
                                                onChange={(e) => updateAsset(selectedAssetType, asset.id, 'valuer', e.target.value)}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                placeholder="Enter valuer name and credentials"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">Upload Valuation Report</label>
                                            <input
                                                type="file"
                                                accept=".pdf,.jpg,.jpeg,.png"
                                                onChange={(e) => {
                                                    if (e.target.files && e.target.files[0]) {
                                                        updateAsset(selectedAssetType, asset.id, 'valuationReport', e.target.files[0].name);
                                                        alert(`File "${e.target.files[0].name}" uploaded successfully`);
                                                    }
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                            />
                                            {asset.valuationReport && (
                                                <p className="text-xs text-green-600 mt-1">✓ {asset.valuationReport}</p>
                                            )}
                                        </div>
                                        
                                        {/* Property-specific fields: Rental Yield & Capital Growth */}
                                        {selectedAssetType === 'property' && (
                                            <>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Rental Yield (% p.a.)
                                                        <span className="ml-1 text-xs text-gray-500">(default: 4.5%)</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.1"
                                                        value={asset.yield || ''}
                                                        onChange={(e) => updateAsset(selectedAssetType, asset.id, 'yield', parseFloat(e.target.value) || 0)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        placeholder="4.5"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Capital Growth (% p.a.)
                                                        <span className="ml-1 text-xs text-gray-500">(default: 4.0%)</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.1"
                                                        value={asset.capitalGrowth || ''}
                                                        onChange={(e) => updateAsset(selectedAssetType, asset.id, 'capitalGrowth', parseFloat(e.target.value) || 0)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        placeholder="4.0"
                                                    />
                                                </div>
                                            </>
                                        )}
                                        
                                        {/* LRBA Loan Details for Property */}
                                        {selectedAssetType === 'property' && (
                                            <>
                                                <div className="col-span-2 mt-4 pt-4 border-t border-gray-200">
                                                    <h4 className="text-sm font-semibold text-gray-800 mb-3 flex items-center">
                                                        <span className="mr-2">🏦</span>
                                                        Limited Recourse Borrowing Arrangement (LRBA)
                                                    </h4>
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Loan Amount ($)
                                                        <span className="ml-1 text-xs text-gray-500">(0 if no loan)</span>
                                                    </label>
                                                    <input
                                                        {...getNumericInputProps()}
                                                        value={asset.loanAmount || ''}
                                                        onChange={(e) => updateAsset(selectedAssetType, asset.id, 'loanAmount', parseFloat(e.target.value) || 0)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        placeholder="0"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Loan-to-Value Ratio (%)
                                                        <span className="ml-1 text-xs text-gray-500">(max 67%)</span>
                                                    </label>
                                                    <input
                                                        {...getNumericInputProps()}
                                                        value={asset.loanToValue || ''}
                                                        onChange={(e) => updateAsset(selectedAssetType, asset.id, 'loanToValue', parseFloat(e.target.value) || 0)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        placeholder="0"
                                                    />
                                                    {asset.loanToValue > 67 && (
                                                        <p className="mt-1 text-xs text-red-600">⚠️ Exceeds ATO maximum of 67%</p>
                                                    )}
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Interest Rate (% p.a.)
                                                        <span className="ml-1 text-xs text-gray-500">(default: 6.25%)</span>
                                                    </label>
                                                    <input
                                                        type="number"
                                                        step="0.01"
                                                        value={asset.interestRate || ''}
                                                        onChange={(e) => updateAsset(selectedAssetType, asset.id, 'interestRate', parseFloat(e.target.value) || 0)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        placeholder="6.25"
                                                    />
                                                </div>
                                                <div>
                                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                                        Loan Term (years)
                                                        <span className="ml-1 text-xs text-gray-500">(default: 25)</span>
                                                    </label>
                                                    <input
                                                        {...getNumericInputProps()}
                                                        value={asset.loanTerm || ''}
                                                        onChange={(e) => updateAsset(selectedAssetType, asset.id, 'loanTerm', parseFloat(e.target.value) || 0)}
                                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                        placeholder="25"
                                                    />
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <div className="mt-4 flex justify-between items-center">
                                        <div className="text-sm text-gray-600">
                                            Gain/Loss: 
                                            <span className={`ml-1 font-medium ${
                                                asset.currentValue > asset.purchasePrice ? 'text-green-600' : 'text-red-600'
                                            }`}>
                                                ${(asset.currentValue - asset.purchasePrice).toLocaleString()} 
                                                (${(((asset.currentValue - asset.purchasePrice) / asset.purchasePrice) * 100).toFixed(1)}%)
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => deleteAsset(selectedAssetType, asset.id)}
                                            className="px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors text-sm"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Valuation Summary Outputs */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold">Valuation Summary</h3>
                    <button
                        onClick={onAllocateToMembers}
                        className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 transition shadow-md text-sm font-medium"
                    >
                        📊 Allocate to Members
                    </button>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {/* Total Fund Value */}
                    <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="text-sm text-gray-600 mb-1">Total Fund Value</div>
                        <div className="text-2xl font-bold text-blue-700">
                            ${totalPortfolioValue.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Sum of all assets</div>
                    </div>
                    
                    {/* Capital Gain/Loss */}
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="text-sm text-gray-600 mb-1">Capital Gain/Loss</div>
                        <div className="text-2xl font-bold text-green-700">
                            ${(() => {
                                const totalGain = Object.values(assets).flat().reduce((sum, asset) => 
                                    sum + (asset.currentValue - asset.purchasePrice), 0);
                                return totalGain.toLocaleString();
                            })()}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Across all assets</div>
                    </div>
                    
                    {/* Total Assets Count */}
                    <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
                        <div className="text-sm text-gray-600 mb-1">Total Assets</div>
                        <div className="text-2xl font-bold text-purple-700">
                            {Object.values(assets).flat().length}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Across all types</div>
                    </div>
                    
                    {/* Review Due Dates */}
                    <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                        <div className="text-sm text-gray-600 mb-1">Stale Valuations</div>
                        <div className="text-2xl font-bold text-orange-700">
                            {complianceIssues.filter(issue => issue.type === 'Overdue').length}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">Overdue (&gt;1 year)</div>
                    </div>
                </div>
                
                {/* Valuation Summary Table */}
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="px-4 py-2 text-left">Asset</th>
                                <th className="px-4 py-2 text-left">Type</th>
                                <th className="px-4 py-2 text-right">Purchase Price</th>
                                <th className="px-4 py-2 text-right">Current Value</th>
                                <th className="px-4 py-2 text-right">Gain/Loss</th>
                                <th className="px-4 py-2 text-left">Last Valued</th>
                            </tr>
                        </thead>
                        <tbody>
                            {Object.entries(assets).flatMap(([type, assetList]) =>
                                assetList.map((asset) => (
                                    <tr key={`${type}-${asset.id}`} className="border-b hover:bg-gray-50">
                                        <td className="px-4 py-2">{asset.name || asset.description || 'Unnamed'}</td>
                                        <td className="px-4 py-2">{assetTypes[type].name}</td>
                                        <td className="px-4 py-2 text-right">${asset.purchasePrice.toLocaleString()}</td>
                                        <td className="px-4 py-2 text-right">${asset.currentValue.toLocaleString()}</td>
                                        <td className={`px-4 py-2 text-right font-medium ${
                                            asset.currentValue > asset.purchasePrice ? 'text-green-600' : 'text-red-600'
                                        }`}>
                                            ${(asset.currentValue - asset.purchasePrice).toLocaleString()}
                                        </td>
                                        <td className="px-4 py-2">{asset.valuationDate || 'Not set'}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
                
                {/* Export Valuation Report */}
                <div className="mt-4 flex justify-end">
                    <button
                        onClick={() => {
                            const doc = new jsPDF();
                            
                            // Title
                            doc.setFontSize(18);
                            doc.setFont(undefined, 'bold');
                            doc.text('SMSF Asset Valuation Report', 105, 20, { align: 'center' });
                            
                            doc.setFontSize(10);
                            doc.setFont(undefined, 'normal');
                            doc.text(`Generated: ${new Date().toLocaleDateString('en-AU')}`, 105, 28, { align: 'center' });
                            
                            let y = 40;
                            
                            // Summary
                            doc.setFontSize(12);
                            doc.setFont(undefined, 'bold');
                            doc.text('Portfolio Summary', 14, y);
                            y += 8;
                            doc.setFontSize(9);
                            doc.setFont(undefined, 'normal');
                            doc.text(`Total Fund Value: $${totalPortfolioValue.toLocaleString()}`, 14, y);
                            y += 5;
                            const totalGain = Object.values(assets).flat().reduce((sum, asset) => 
                                sum + (asset.currentValue - asset.purchasePrice), 0);
                            doc.text(`Total Capital Gain/Loss: $${totalGain.toLocaleString()}`, 14, y);
                            y += 5;
                            doc.text(`Total Assets: ${Object.values(assets).flat().length}`, 14, y);
                            y += 10;
                            
                            // Asset Details
                            doc.setFontSize(12);
                            doc.setFont(undefined, 'bold');
                            doc.text('Asset Details', 14, y);
                            y += 8;
                            
                            Object.entries(assets).forEach(([type, assetList]) => {
                                if (assetList.length > 0) {
                                    doc.setFontSize(10);
                                    doc.setFont(undefined, 'bold');
                                    doc.text(assetTypes[type].name, 14, y);
                                    y += 6;
                                    
                                    assetList.forEach((asset) => {
                                        doc.setFontSize(9);
                                        doc.setFont(undefined, 'normal');
                                        doc.text(`• ${asset.name || asset.description || 'Unnamed'}`, 18, y);
                                        y += 4;
                                        doc.text(`  Purchase: $${asset.purchasePrice.toLocaleString()} | Current: $${asset.currentValue.toLocaleString()} | Gain/Loss: $${(asset.currentValue - asset.purchasePrice).toLocaleString()}`, 18, y);
                                        y += 4;
                                        doc.text(`  Valuation Date: ${asset.valuationDate || 'Not set'} | Method: ${asset.valuationMethod}`, 18, y);
                                        y += 6;
                                        
                                        if (y > 270) {
                                            doc.addPage();
                                            y = 20;
                                        }
                                    });
                                    y += 4;
                                }
                            });
                            
                            // Disclaimer
                            doc.setFontSize(8);
                            doc.setFont(undefined, 'italic');
                            doc.setTextColor(100);
                            const disclaimer = 'This valuation report is for information purposes only. Independent professional valuations may be required for compliance.';
                            doc.text(disclaimer, 14, 280);
                            
                            doc.save(`SMSF_Valuation_Report_${new Date().toISOString().split('T')[0]}.pdf`);
                        }}
                        className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                        Export Valuation Report (PDF)
                    </button>
                </div>
            </div>

            {/* Compliance Requirements */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Compliance Requirements</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-2">In-House Asset Rules</h4>
                        <div className={`p-3 rounded-lg ${
                            inHouseAssetCompliance.isCompliant ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'
                        }`}>
                            <p className={`text-sm ${
                                inHouseAssetCompliance.isCompliant ? 'text-green-700' : 'text-red-700'
                            }`}>
                                {inHouseAssetCompliance.isCompliant 
                                    ? `✅ Compliant: ${inHouseAssetCompliance.percentage.toFixed(1)}% (Limit: 5%)`
                                    : `⚠️ Non-compliant: ${inHouseAssetCompliance.percentage.toFixed(1)}% (Limit: 5%)`
                                }
                            </p>
                            {!inHouseAssetCompliance.isCompliant && (
                                <p className="text-sm text-red-600 mt-1">
                                    Over limit by ${(inHouseAssetCompliance.overLimit * totalPortfolioValue / 100).toLocaleString()}
                                </p>
                            )}
                        </div>
                    </div>
                    <div>
                        <h4 className="font-semibold text-gray-700 mb-2">Valuation Requirements</h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                            <li>• Annual valuations required for all assets</li>
                            <li>• Independent valuations for assets over $300K</li>
                            <li>• Document all valuation methods used</li>
                            <li>• Maintain valuation records for 5 years</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Advanced Options */}
            {showAdvanced && (
                <div className="bg-white p-6 rounded-lg shadow-md">
                    <h3 className="text-lg font-semibold mb-4">Advanced Options</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h4 className="font-semibold text-gray-700 mb-2">Valuation Settings</h4>
                            <div className="space-y-3">
                                <label className="flex items-center">
                                    <input type="checkbox" className="mr-2" />
                                    <span className="text-sm">Enable automatic valuation reminders</span>
                                </label>
                                <label className="flex items-center">
                                    <input type="checkbox" className="mr-2" />
                                    <span className="text-sm">Include inflation adjustments</span>
                                </label>
                                <label className="flex items-center">
                                    <input type="checkbox" className="mr-2" />
                                    <span className="text-sm">Track valuation history</span>
                                </label>
                            </div>
                        </div>
                        <div>
                            <h4 className="font-semibold text-gray-700 mb-2">Export Options</h4>
                            <div className="space-y-2">
                                <button className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm">
                                    Export Asset Register
                                </button>
                                <button className="w-full px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm">
                                    Export Valuation Report
                                </button>
                                <button className="w-full px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm">
                                    Export Compliance Report
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SMSFAssetValuationManager;

